import React, { useMemo } from 'react';
import { Flex } from 'core/components';
import { observer } from 'mobx-react';
import UdeDataView from 'app/views/ude/UdeDataView';
import QueryModel, { QuerySettings } from 'app/stores/ude/QueryModel';
import type { Query, QueryResults } from '@kentik/ui-shared/query/query.types';
import ToolResultCard from '../components/ToolResultCard';
import ViewInDashboardButton from './ViewInDashboardButton';
import ViewInUdeButton from './ViewInUdeButton';

interface UdeToolResultProps {
  tool: {
    id?: string | number;
    tool_tracking_id?: string | number;
    title?: string;
    tool_name?: string;
    query_object?: Query;
    raw_result?: {
      query?: Query;
      results?: QueryResults;
    };
  };
  chartHeight?: number;
  onCloseToolResult?: () => void;
  isInlineMode?: boolean;
}

function UdeToolResult({ tool, chartHeight = 500, onCloseToolResult, isInlineMode }: UdeToolResultProps) {
  const rawData = tool.raw_result || {};
  const query = rawData.query || tool.query_object;
  const { results } = rawData;

  const queryModel = useMemo(() => {
    if (!query) {
      return null;
    }

    const settings: QuerySettings = {
      chart: {
        enable: true,
        type: query.viz?.type || 'stackedArea',
        colors: 'default',
        legend: { enable: true, position: 'bottom', mode: 'table', showRollups: true },
        xAxis: { timezone: 'local' },
        yAxis: { type: 'linear' }
      }
    };

    return new QueryModel(query, settings, { results });
  }, [query, results]);

  if (!tool || !query || !queryModel) {
    return <Flex>Invalid tool data for UDE Query.</Flex>;
  }

  return (
    <ToolResultCard
      tool={tool}
      onCloseToolResult={onCloseToolResult}
      isInlineMode={isInlineMode}
      className="tool-result-ude"
      title={tool.title}
      minHeight={chartHeight}
      headerActions={
        <Flex gap={1}>
          <ViewInUdeButton query={query} />
          <ViewInDashboardButton query={query} title={tool.title} />
        </Flex>
      }
    >
      <Flex
        flexDirection="column"
        flex={isInlineMode ? '0 0 auto' : 1}
        height={chartHeight}
        overflow="hidden"
        position="relative"
      >
        <UdeDataView queryModel={queryModel} />
      </Flex>
    </ToolResultCard>
  );
}

export default observer(UdeToolResult);
